/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useMemo, type JSX } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';

import type { ShopperSearch } from '@/scapi';
import { Button } from '@/components/ui/button';
import { X as Close } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { useStoreLocator } from '@/extensions/store-locator/providers/store-locator';
import { type TFunction } from 'i18next';
// @sfdc-extension-block-end SFDC_EXT_BOPIS

// @sfdc-extension-block-start SFDC_EXT_BOPIS
const getIlidsLabel = (
    selectedStoreInfo: { inventoryId?: string; name?: string } | null,
    tBopis: TFunction<'extBopis'>
): string => {
    if (selectedStoreInfo?.name) {
        return tBopis('storeInventoryFilter.label', { storeName: selectedStoreInfo.name });
    }
    return tBopis('storeInventoryFilter.inStock');
};
// @sfdc-extension-block-end SFDC_EXT_BOPIS

export default function CategoryFilters({
    result,
    className,
    refine,
    hiddenRefinementAttributeIds = [],
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
    className?: string;
    refine?: string[];
    hiddenRefinementAttributeIds?: string[];
}): JSX.Element | null {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const refinements = useMemo(() => result?.refinements || [], [result]);
    const hiddenRefinementAttributes = useMemo(
        () => new Set(hiddenRefinementAttributeIds),
        [hiddenRefinementAttributeIds]
    );

    /**
     * Optimistic search string derived from the in-flight navigation target.
     *
     * While a navigation is pending, `navigation.location` holds the target location, allowing us to read the
     * intended refinements immediately. Once the navigation settles, we fall back to the current location.
     */
    const effectiveRefines = useMemo(() => {
        if (navigation.location) {
            return new URLSearchParams(navigation.location.search).getAll('refine');
        }

        return refine ?? new URLSearchParams(location.search).getAll('refine');
    }, [location.search, navigation.location, refine]);

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const selectedStoreInfo = useStoreLocator((s) => s.selectedStoreInfo);
    const { t: tBopis } = useTranslation('extBopis');
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    const activeFilters = useMemo(() => {
        const filters: Array<{
            attributeId: string;
            value: string;
        }> = [];

        for (const refineValue of effectiveRefines) {
            const separatorIndex = refineValue.indexOf('=');
            if (separatorIndex === -1) continue;

            const attributeId = refineValue.substring(0, separatorIndex);
            const value = refineValue.substring(separatorIndex + 1);
            // Category selection is represented by horizontal navigation, so keep cgid in URL for behavior
            // and clear-all, but hide it from applied-filter chips.
            if (attributeId === 'cgid' || hiddenRefinementAttributes.has(attributeId)) continue;

            filters.push({
                attributeId,
                value,
            });
        }
        return filters;
    }, [effectiveRefines, hiddenRefinementAttributes]);

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const ilidsFilters = useMemo(
        () =>
            activeFilters
                .filter((filter) => filter.attributeId === 'ilids')
                .map((filter) => ({
                    ...filter,
                    valueLabel: getIlidsLabel(selectedStoreInfo, tBopis),
                })),
        [activeFilters, selectedStoreInfo, tBopis]
    );
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // Clear all filters
    const clearAllFilters = useCallback(() => {
        const params = new URLSearchParams(location.search);
        params.delete('refine');
        params.set('offset', '0');

        void navigate({
            ...location,
            search: `?${params.toString()}`,
        });
    }, [location, navigate]);

    const groupedFilters = useMemo(() => {
        const groups = new Map<
            string,
            {
                attributeId: string;
                attributeLabel: string;
                values: string[];
            }
        >();

        for (const filter of activeFilters) {
            // @sfdc-extension-line SFDC_EXT_BOPIS
            if (filter.attributeId === 'ilids') continue;

            const refinement = refinements.find((r) => r.attributeId === filter.attributeId);
            const attributeLabel = refinement?.label || filter.attributeId;
            const existing = groups.get(filter.attributeId);

            if (existing) {
                existing.values.push(filter.value);
                continue;
            }

            groups.set(filter.attributeId, {
                attributeId: filter.attributeId,
                attributeLabel,
                values: [filter.value],
            });
        }

        return Array.from(groups.values());
    }, [activeFilters, refinements]);

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const removeFilter = useCallback(
        (attributeId: string, value: string) => {
            const params = new URLSearchParams(location.search);
            const refines = params.getAll('refine');
            const refinePair = `${attributeId}=${value}`;

            params.delete('refine');
            refines.filter((r) => r !== refinePair).forEach((r) => params.append('refine', r));

            params.set('offset', '0');
            return navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate]
    );
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // Remove all values for a specific attribute
    const removeAttributeFilters = useCallback(
        (attributeId: string) => {
            const params = new URLSearchParams(location.search);
            const refines = params.getAll('refine');

            params.delete('refine');
            refines.filter((r) => !r.startsWith(`${attributeId}=`)).forEach((r) => params.append('refine', r));

            params.set('offset', '0');
            return navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate]
    );

    // Don't render if no active filters
    if (activeFilters.length === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                'flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
                className
            )}>
            {/* @sfdc-extension-block-start SFDC_EXT_BOPIS */}
            {ilidsFilters.map(({ attributeId, value, valueLabel }) => (
                <Button
                    key={`${attributeId}:${value}`}
                    variant="outline"
                    size="sm"
                    className="shrink-0 cursor-pointer rounded-none bg-background hover:bg-muted"
                    onClick={() => void removeFilter(attributeId, value)}>
                    <span className="mr-1 whitespace-nowrap">{valueLabel}</span>
                    <Close className="size-3" />
                </Button>
            ))}
            {/* @sfdc-extension-block-end SFDC_EXT_BOPIS */}
            {groupedFilters.map(({ attributeId, attributeLabel, values }) => (
                <Button
                    key={attributeId}
                    variant="outline"
                    size="sm"
                    className="shrink-0 cursor-pointer rounded-none bg-background hover:bg-muted"
                    onClick={() => void removeAttributeFilters(attributeId)}>
                    <span className="mr-1 whitespace-nowrap">
                        {attributeLabel} ({values.length})
                    </span>
                    <Close className="size-3" />
                </Button>
            ))}
            <Button
                variant="link"
                className="shrink-0 m-0 p-0 cursor-pointer underline text-sm text-muted-foreground hover:text-foreground"
                onClick={clearAllFilters}>
                {t('categoryRefinements:clearAll')}
            </Button>
        </div>
    );
}
